import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useAuth } from '../../../context/AuthContext';
import withPermission from '../../../utils/withPermission';
import ReactPaginate from 'react-paginate';
import { Offcanvas } from "react-bootstrap";

const PendingLoan = () => {
    const [pendingLoan, setPendingLoan] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [formData, setFormData] = useState({ name: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const didFetchRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
 
    const WithDeletePermission = withPermission(({ children }) => children, ['loan-delete']);
    const [searchQuery, setSearchQuery] = useState('');
   
    const debounceTimeoutRef = useRef(null);
    const [approvalComment, setApprovalComment] = useState('');

    const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/loans';

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchPendingLoan(1, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const fetchPendingLoan = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/loans?status=pending`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });
            setPendingLoan(response.data?.data?.data || []);
            setPageCount(response.data.last_page);
            setCurrentPage(response.data.current_page);
        } catch (error) {
            console.error('Error fetching pending Loan:', error);
        }
    };

    const handleViewLoan = (Loan) => {
        setSelectedLoan(Loan);
        setShowApprovalModal(true);
    };

    const handleApproveReject = async (status) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.put(
                `${backendUrl}/api/loans/${selectedLoan.id}/status`,
                {
                    status: status,
                    manager_comment: approvalComment
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                }
            );

            if (response.data.success) {
                Swal.fire(
                    "Success!",
                    `Loan has been ${status}.`,
                    "success"
                );
                setShowApprovalModal(false);
                setApprovalComment('');
                fetchPendingLoan(currentPage);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error updating Loan status:', error);
            Swal.fire(
                "Error!",
                "Failed to update Loan status. Please try again.",
                "error"
            );
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    setPendingLoan(pendingLoan.filter(Loan => Loan.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The Loan has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting Loan:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete Loan. Please try again.",
                        "error"
                    );
                }
            }
        });
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchPendingLoan(newPage);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="badge bg-success">Approved</span>;
            case 'rejected':
                return <span className="badge bg-danger">Rejected</span>;
            default:
                return <span className="badge bg-warning">Pending</span>;
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Pending Loan Requests</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search by employee name..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <table className="table align-middle table-nowrap table-check table-list">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '20px' }} className="align-middle">
                                                    <div className="form-check font-size-16">
                                                        <input className="form-check-input" type="checkbox" id="checkAll" />
                                                        <label className="form-check-label" htmlFor="checkAll"></label>
                                                    </div>
                                                </th>
                                                <th className="align-middle">#</th>
                                                <th className="align-middle">Apply Date</th>
                                                <th className="align-middle">Employee</th>
                                                <th className="align-middle">Loan Amount</th>
                                                <th className="align-middle">Monthly Installment</th>
                                                <th className="align-middle">Status</th>
                                                <th className="align-middle">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingLoan.map((Loan, index) => (
                                                <tr key={Loan.id}>
                                                    <td>
                                                        <div className="form-check font-size-16">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`instanceCheck${index}`}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`instanceCheck${index}`}
                                                            ></label>
                                                        </div>
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{Loan.start_date}</td>
                                                    <td>{Loan.employee?.first_name} {Loan.employee?.last_name}</td>
                                                    <td>{Loan.loan_amount}</td>
                                                    <td>
                                                        {Loan.installment_amount}
                                                       
                                                    </td>

                                                    <td>{getStatusBadge(Loan.status)}</td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <i
                                                                className="mdi mdi-dots-horizontal font-size-18"
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                                style={{ cursor: 'pointer' }}
                                                            ></i>
                                                            <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => handleViewLoan(Loan)}
                                                                    >
                                                                        <i className="mdi mdi-eye me-2"></i>View Details
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <WithDeletePermission>
                                                                        <button
                                                                            className="dropdown-item text-danger"
                                                                            onClick={() => handleDelete(Loan.id)}
                                                                        >
                                                                            <i className="mdi mdi-delete me-2"></i>Delete
                                                                        </button>
                                                                    </WithDeletePermission>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {pendingLoan.length === 0 && (
                                        <div className="text-center py-4">
                                            <i className="mdi mdi-inbox-outline display-4 text-muted"></i>
                                            <p className="mt-2">No pending Loan requests found</p>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>

                                    {/* Approval Modal */}
                                    <Offcanvas
                                        show={showApprovalModal}
                                        onHide={() => {
                                            setShowApprovalModal(false);
                                            setApprovalComment('');
                                        }}
                                        placement="end"
                                        style={{ width: '500px' }}
                                        className="uae-erps-offcanvas"
                                    >
                                        <Offcanvas.Header className="border-bottom px-4 py-3" closeButton>
                                            <Offcanvas.Title className="d-flex align-items-center">
                                                <i className="mdi mdi-file-document-outline me-2 text-primary"></i>
                                                <span className="fw-semibold">Loan Request Details</span>
                                            </Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <Offcanvas.Body className="p-4">
                                            <div className="mb-4">
                                                <h6 className="text-uppercase text-muted small fw-bold mb-3">EMPLOYEE INFORMATION</h6>
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Employee Name</label>
                                                        <p className="mb-0 fw-medium">{selectedLoan?.employee?.first_name} {selectedLoan?.employee?.last_name}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Application Date</label>
                                                        <p className="mb-0 fw-medium">{selectedLoan?.start_date}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <h6 className="text-uppercase text-muted small fw-bold mb-3">Loan DETAILS</h6>
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Loan</label>
                                                        <p className="mb-0 fw-medium">{selectedLoan?.title}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Total Month</label>
                                                        <p>
                                                            {selectedLoan?.total_month}
                                                           
                                                        </p>

                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                     <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Total Loan Amount</label>
                                                        <p>
                                                            {selectedLoan?.loan_amount}
                                                           
                                                        </p>

                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label text-muted small mb-1">Installment Amount</label>
                                                        <p className="mb-0 fw-medium">{selectedLoan?.installment_amount}</p>
                                                    </div>
                                                   
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <h6 className="text-uppercase text-muted small fw-bold mb-3">Description</h6>
                                                <div className="bg-light rounded p-3">
                                                    <p className="mb-0">{selectedLoan?.remarks || 'No reason provided'}</p>
                                                </div>
                                            </div>

                                            {selectedLoan?.attachment && (
                                                <div className="mb-4">
                                                    <h6 className="text-uppercase text-muted small fw-bold mb-3">ATTACHMENT</h6>
                                                    <a
                                                        href={`${process.env.REACT_APP_BACKEND_URL}/storage/${selectedLoan.attachment}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                                                    >
                                                        <i className="mdi mdi-paperclip me-1"></i>Download Attachment
                                                    </a>
                                                </div>
                                            )}

                                            {/* <div className="mb-4">
                                                <h6 className="text-uppercase text-muted small fw-bold mb-3">MANAGER'S COMMENT</h6>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    placeholder="Add your comment (optional)"
                                                    value={approvalComment}
                                                    onChange={(e) => setApprovalComment(e.target.value)}
                                                    style={{ resize: 'none' }}
                                                ></textarea>
                                            </div> */}

                                            <div className="border-top pt-3 mt-4">
                                                <div className="d-flex justify-content-end gap-2">
                                                    {/* <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => {
                                                            setShowApprovalModal(false);
                                                            setApprovalComment('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </button> */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger d-flex align-items-center"
                                                        onClick={() => handleApproveReject('rejected')}
                                                    >
                                                        <i className="mdi mdi-close-circle-outline me-1"></i>Reject
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success d-flex align-items-center"
                                                        onClick={() => handleApproveReject('approved')}
                                                    >
                                                        <i className="mdi mdi-check-circle-outline me-1"></i>Approve
                                                    </button>
                                                </div>
                                            </div>
                                        </Offcanvas.Body>

                                        <style>
                                            {`
        .uae-erps-offcanvas {
            box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
            border-left: 1px solid #e9ecef;
        }
        
        .uae-erps-offcanvas .offcanvas-header {
            background-color: #f8f9fa;
        }
        
        .uae-erps-offcanvas .btn-close {
            font-size: 0.75rem;
            padding: 0.5rem;
            background-size: 0.75rem;
        }
        
        .uae-erps-offcanvas .offcanvas-title {
            font-size: 1.1rem;
        }
        
        .uae-erps-offcanvas .form-control:focus {
            border-color: #405189;
            box-shadow: 0 0 0 0.15rem rgba(64, 81, 137, 0.25);
        }
        
        .uae-erps-offcanvas .btn-success {
            background-color: #0ab39c;
            border-color: #0ab39c;
        }
        
        .uae-erps-offcanvas .btn-success:hover {
            background-color: #099885;
            border-color: #099885;
        }
        
        .uae-erps-offcanvas .btn-danger {
            background-color: #f06548;
            border-color: #f06548;
        }
        
        .uae-erps-offcanvas .btn-danger:hover {
            background-color: #ee4c2c;
            border-color: #ee4c2c;
        }
        
        .uae-erps-offcanvas .btn-outline-primary {
            color: #405189;
            border-color: #405189;
        }
        
        .uae-erps-offcanvas .btn-outline-primary:hover {
            background-color: #405189;
            border-color: #405189;
            color: white;
        }
        
        @media (max-width: 576px) {
            .uae-erps-offcanvas {
                width: 100% !important;
            }
        }
        `}
                                        </style>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PendingLoan;